import { useEffect, useState, useSyncExternalStore } from "react";
import { BffUnauthenticatedError, bffJson } from "@/shared/api/client";
import { currentSession, getSessionSnapshot, subscribeSession } from "@/shared/api/session";

export interface Access {
  superuser: boolean;
  quantAdmin: boolean;
  userAdmin: boolean;
  flags: Readonly<Record<string, boolean>>;
  unread?: true;
}

export function useAccess(): Access | null {
  const session = useSyncExternalStore(subscribeSession, getSessionSnapshot);
  const userId = session.status === "authenticated" ? (session.session?.userId ?? null) : null;
  const [loaded, setLoaded] = useState<{ userId: string; access: Access } | null>(null);

  useEffect(() => {
    if (!userId) return;
    let live = true;
    loadAccess().then(
      (access) => live && setLoaded({ userId, access }),
      () => live && setLoaded({ userId, access: UNREAD_ACCESS }),
    );
    return () => {
      live = false;
    };
  }, [userId]);

  if (!userId) return NO_ACCESS;
  return loaded?.userId === userId ? loaded.access : null;
}

export function loadAccess(): Promise<Access> {
  const session = currentSession();
  if (!session) return Promise.reject(new BffUnauthenticatedError());
  if (cache && cache.userId === session.userId) return cache.promise;

  const promise = readAccess();
  const entry = { userId: session.userId, promise };
  cache = entry;
  promise.catch(() => {
    if (cache === entry) cache = null;
  });
  return promise;
}

export function getFaAdminAccess(): Promise<boolean> {
  return loadAccess().then((access) => access.flags.fa === true && access.superuser);
}

export function accessFrom(
  flags: FeatureFlagResponse,
  grants: readonly EffectiveGrantResponse[],
): Access {
  const superuser = isSuperuserGrant(grants);
  return {
    superuser,
    quantAdmin: superuser || holdsGrant(grants, QUANT_ADMIN),
    userAdmin: superuser || holdsGrant(grants, USER_CREATE),
    flags: Object.fromEntries(
      Object.entries(flags ?? {}).map(([name, value]) => [name, value === true]),
    ),
  };
}

export function isSuperuserGrant(grants: readonly EffectiveGrantResponse[]): boolean {
  return grants.some(
    (grant) => grant.permission_identifier?.code === SUPERUSER && grant.scope === "FULL",
  );
}

async function readAccess(): Promise<Access> {
  const [flags, grants] = await Promise.all([
    bffJson<FeatureFlagResponse>("v1/feature-flag"),
    bffJson<EffectiveGrantResponse[]>("v1/permission/effective-grant/user"),
  ]);
  return accessFrom(flags, Array.isArray(grants) ? grants : []);
}

function holdsGrant(grants: readonly EffectiveGrantResponse[], code: string): boolean {
  return grants.some(
    (grant) =>
      grant.permission_identifier?.code === code && grant.scope != null && grant.scope !== "NONE",
  );
}

export interface EffectiveGrantResponse {
  permission_identifier?: { id?: string; code?: string } | null;
  scope?: "FULL" | "PARTIAL" | "NONE" | null;
}

export type FeatureFlagResponse = Record<string, boolean | null | undefined>;

const SUPERUSER = "SUPERUSER";
const QUANT_ADMIN = "QUANT_ADMIN";
const USER_CREATE = "USER_CREATE";

const NO_ACCESS: Access = { superuser: false, quantAdmin: false, userAdmin: false, flags: {} };
const UNREAD_ACCESS: Access = { ...NO_ACCESS, unread: true };

let cache: { userId: string; promise: Promise<Access> } | null = null;
